import {
    getCurrentStep,
    getTotalSteps,
    setupMultistepEvents,
    showStep,
    type MultistepButtons,
} from '../forms/multistep';
import { PhonePreparator } from '../forms/phone';
import { setupValidationEvents, validateStepInputs } from '../forms/validation';

// The intl-tel-input library is loaded via plain <script> tags on the page
// (see quiz.blade.php). It registers a global `intlTelInput` function.
declare const intlTelInput: unknown;

/**
 * Fetch the score from the server (preview endpoint) and write it into the
 * heading on the contact step. The server uses the same QuizScoringService
 * that runs on submit, so the displayed number matches the final result —
 * single source of truth, no JS-side scoring logic to keep in sync.
 */
async function fetchAndUpdateScore(form: HTMLFormElement): Promise<void> {
    const scoreEl = form.querySelector<HTMLElement>('.js-quiz-score');
    const url     = form.dataset.previewUrl;

    if (!scoreEl || !url) {
        return;
    }

    const body = new FormData();

    for (let i = 1; i <= 17; i++) {
        const selected = form.querySelector<HTMLInputElement>(`input[name="q${i}"]:checked`);

        if (selected) {
            body.append(`q${i}`, selected.value);
        }
    }

    const csrfToken = form.querySelector<HTMLInputElement>('input[name="_token"]')?.value;

    if (csrfToken) {
        body.append('_token', csrfToken);
    }

    try {
        const response = await fetch(url, {
            method: 'POST',
            body,
            headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
            return;
        }

        const json = await response.json() as { score?: number };

        if (typeof json.score === 'number') {
            scoreEl.textContent = String(json.score);
        }
    } catch {
        // Silent fail — the placeholder "--" stays as-is.
    }
}

function updateCompanyName(form: HTMLFormElement): void {
    const target = form.querySelector<HTMLElement>('.js-quiz-company');
    const input  = form.querySelector<HTMLInputElement>('input[name="company_name"]');

    if (!target || !input) {
        return;
    }

    const name = input.value.trim();

    if (name) {
        target.textContent = name;
    }
}

function initIntroToggle(intro: HTMLElement, form: HTMLFormElement): void {
    const startBtn = intro.querySelector<HTMLButtonElement>('.js-quiz-start');

    if (!startBtn) {
        return;
    }

    startBtn.addEventListener('click', () => {
        intro.classList.add('started');
        form.classList.remove('d-none');
    });
}

function updateNavButtonState(form: HTMLFormElement, buttons: MultistepButtons): void {
    const currentStep = getCurrentStep(form);
    const totalSteps = getTotalSteps(form);
    const stepContents = form.querySelectorAll<HTMLElement>('.step-content > div:not(.btn-container)');
    const currentContent = stepContents[currentStep];
    const valid = validateStepInputs(currentContent);

    const button = currentStep === totalSteps - 1 ? buttons.submitBtn : buttons.nextBtn;

    if (button) {
        button.disabled = !valid;
    }
}

function clearSubmitErrors(form: HTMLFormElement): void {
    form.querySelector('.js-quiz-errors')?.remove();
}

function showSubmitErrors(form: HTMLFormElement, errors: Record<string, string[] | string>): void {
    clearSubmitErrors(form);

    const errorBlock = document.createElement('div');
    errorBlock.className = 'alert alert-danger js-quiz-errors';

    const ul = document.createElement('ul');
    ul.className = 'mb-0';

    for (const value of Object.values(errors)) {
        const messages = Array.isArray(value) ? value : [value];

        for (const message of messages) {
            const li = document.createElement('li');
            li.textContent = String(message);
            ul.appendChild(li);
        }
    }

    errorBlock.appendChild(ul);
    form.prepend(errorBlock);
    errorBlock.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function initAjaxSubmit(form: HTMLFormElement, buttons: MultistepButtons): void {
    form.addEventListener('submit', (e: Event) => {
        e.preventDefault();

        if (buttons.submitBtn) {
            buttons.submitBtn.disabled = true;
        }

        clearSubmitErrors(form);

        fetch(form.action, {
            method: 'POST',
            body: new FormData(form),
            headers: { Accept: 'application/json' },
        })
            .then(async (response) => {
                if (response.ok) {
                    // Server flashed quizResult into session and handed back the
                    // dedicated results URL — a GET to it renders the results.
                    const body = await response.json().catch(() => ({})) as { resultsUrl?: string | null };

                    window.location.href = body.resultsUrl || window.location.pathname + window.location.search;
                    return;
                }

                const json = await response.json().catch(() => ({}));

                if (response.status === 422 && json && typeof json === 'object' && 'errors' in json) {
                    showSubmitErrors(form, (json as { errors: Record<string, string[]> }).errors);
                } else {
                    showSubmitErrors(form, { _general: ['Submission failed. Please try again.'] });
                }

                if (buttons.submitBtn) {
                    buttons.submitBtn.disabled = false;
                }
            })
            .catch(() => {
                showSubmitErrors(form, { _general: ['Network error. Please try again.'] });

                if (buttons.submitBtn) {
                    buttons.submitBtn.disabled = false;
                }
            });
    });
}

function initPhoneField(form: HTMLFormElement): void {
    if (typeof intlTelInput === 'undefined') {
        // Library script tags failed to load. Skip silently — the field
        // still works as a plain <input type="tel"> with pattern validation.
        return;
    }

    new PhonePreparator(form, {
        initialCountry: form.dataset.locale || 'lv',
    }).initializeFields();
}

function initQuizForm(form: HTMLFormElement): void {
    const buttons: MultistepButtons = {
        nextBtn: null,
        prevBtn: null,
        submitBtn: form.querySelector<HTMLButtonElement>('button[type="submit"]'),
    };

    // multistep.ts re-enables the next button after a 150ms transition delay,
    // so re-check validity afterwards to keep the disabled state in sync.
    setupMultistepEvents(form, buttons, () => {
        window.setTimeout(() => {
            updateNavButtonState(form, buttons);

            // Fetch the live score only when the contact (last) step is shown
            // — the heading there displays it. No need to call the endpoint
            // on every step transition.
            if (getCurrentStep(form) === getTotalSteps(form) - 1) {
                updateCompanyName(form);
                void fetchAndUpdateScore(form);
            }
        }, 200);
    });
    setupValidationEvents(form, () => updateNavButtonState(form, buttons));

    initPhoneField(form);
    initAjaxSubmit(form, buttons);

    showStep(form, 0, buttons);
    updateNavButtonState(form, buttons);
}

function initPrintButton(): void {
    const printBtn = document.querySelector<HTMLButtonElement>('.js-quiz-print');

    if (!printBtn) {
        return;
    }

    printBtn.addEventListener('click', () => window.print());
}

document.addEventListener('DOMContentLoaded', () => {
    const intro = document.querySelector<HTMLElement>('.js-quiz-intro');
    const form = document.querySelector<HTMLFormElement>('.js-quiz-form');

    if (intro && form) {
        initIntroToggle(intro, form);
        initQuizForm(form);
    }

    initPrintButton();
});